import { z } from 'zod';
import { productTypeSchema, slugSchema } from './catalog.js';
import { productSpecsSchema } from './productSpecs.js';

/**
 * Data "Upload SKU": isian bergaya Shopee yang dipakai tim CI. Sebagian
 * mengisi katalog kita (foto, opsi, harga per kombinasi, berat, stok),
 * sebagian hanya dibutuhkan marketplace dan disimpan apa adanya.
 */

/** Kode SKU: huruf besar/kecil, angka, titik, strip, garis bawah. */
export const skuCodeSchema = z
  .string()
  .trim()
  .max(64)
  .regex(/^[A-Za-z0-9._-]*$/, 'Kode SKU hanya boleh huruf, angka, titik, strip, dan garis bawah');

export const VARIANT_STOCK_MAX = 1_000_000;

/**
 * Shopee hanya mengizinkan dua kelompok variasi, tetapi katalog CI punya
 * produk dengan sumbu lebih banyak (kaos DTF: bahan, lengan, warna, ukuran,
 * posisi cetak), dan sumbu itulah yang dipakai mesin harga. Jadi batasnya
 * mengikuti katalog kita, bukan Shopee.
 */
export const MAX_SKU_VARIATIONS = 6;

/** Kombinasi varian maksimal yang boleh disimpan sekali jalan. */
export const MAX_SKU_ROWS = 3000;

/** Satu kombinasi varian: pilihan opsi + stok + kode SKU. */
export const productVariantSchema = z.object({
  /** {optionKey: valueKey}, sama seperti selections di override harga. */
  selections: z.record(z.string().min(1).max(64), z.string().min(1).max(64)),
  sku: skuCodeSchema.default(''),
  stock: z.number().int().min(0).max(VARIANT_STOCK_MAX).default(0),
  /** Nonaktif = kombinasi tidak dijual, walau opsinya ada. */
  isActive: z.boolean().default(true),
});

export const MARKETPLACE_CONDITIONS = ['NEW', 'USED'] as const;
export const DANGEROUS_GOODS = ['NONE', 'BATTERY', 'LIQUID', 'FLAMMABLE'] as const;

export const DANGEROUS_GOODS_LABEL: Record<(typeof DANGEROUS_GOODS)[number], string> = {
  NONE: 'Tidak mengandung barang berbahaya',
  BATTERY: 'Mengandung baterai/magnet',
  LIQUID: 'Mengandung cairan',
  FLAMMABLE: 'Mudah terbakar',
};

/** Kanal kirim marketplace; nilainya hanya dicatat untuk pengisian di Shopee. */
export const MARKETPLACE_SHIPPING_CHANNELS = ['INSTANT', 'SAME_DAY', 'REGULAR', 'CARGO'] as const;

export const MARKETPLACE_SHIPPING_LABEL: Record<
  (typeof MARKETPLACE_SHIPPING_CHANNELS)[number],
  string
> = {
  INSTANT: 'Instan',
  SAME_DAY: 'Sameday',
  REGULAR: 'Reguler',
  CARGO: 'Kargo',
};

/**
 * Isian yang ada di layar Shopee tetapi belum punya padanan di sistem kita.
 * Sengaja disimpan apa adanya (belum disambungkan ke mesin harga, ongkir,
 * atau tampilan web) supaya form terasa sama bagi staf.
 */
export const marketplaceListingSchema = z.object({
  /** Kategori di marketplace, mis. "Hobi & Koleksi > Souvenir > Gantungan Kunci". */
  categoryPath: z.string().trim().max(240).default(''),
  condition: z.enum(MARKETPLACE_CONDITIONS).default('NEW'),
  /** SKU induk (kode produk di marketplace). */
  parentSku: skuCodeSchema.default(''),
  videoUrl: z
    .union([z.literal(''), z.string().trim().url('Tautan video tidak valid').max(500)])
    .default(''),
  /** Pre-order: jumlah hari persiapan; 0/null berarti bukan pre-order. */
  preOrderDays: z.number().int().min(0).max(90).nullable().default(null),
  dangerousGoods: z.enum(DANGEROUS_GOODS).default('NONE'),
  shippingChannels: z.array(z.enum(MARKETPLACE_SHIPPING_CHANNELS)).max(8).default([]),
  /** Asuransi pengiriman (belum terhubung ke perhitungan ongkir). */
  shippingInsurance: z.boolean().default(false),
  /** Jadwal tayang (belum terhubung; status tayang tetap diatur di tab Detail). */
  scheduledAt: z.string().trim().max(40).default(''),
  /** Grosir: harga per jumlah minimum (belum terhubung ke mesin harga; tier tetap di Harga & opsi). */
  wholesale: z
    .array(
      z.object({
        minQty: z.number().int().min(1).max(1_000_000),
        maxQty: z.number().int().min(1).max(1_000_000).nullable().default(null),
        price: z.number().min(0).max(1_000_000_000),
      }),
    )
    .max(20)
    .default([]),
  /** Kode produk universal (GTIN/barcode). Belum dipakai sistem kita. */
  gtin: z.string().trim().max(40).default(''),
  /** Penjual menyatakan produk tidak punya GTIN. */
  noGtin: z.boolean().default(false),
  /** Maksimal pembelian per pesanan (belum ditegakkan di keranjang). */
  maxPurchase: z.number().int().min(1).max(1_000_000).nullable().default(null),
  /** "Atur Dikirim Dalam untuk variasi" (belum terhubung; lead time tetap per produk). */
  perVariantLeadTime: z.boolean().default(false),
  /**
   * Foto per nilai Variasi 1, {nilai: mediaId}. Belum dipakai halaman produk;
   * foto yang tampil di web masih dari daftar foto produk.
   */
  variationImages: z.record(z.string().min(1).max(120), z.string().min(1).max(64)).default({}),
});

/** Satu kelompok variasi ala Shopee: nama + daftar nilai. */
export const skuVariationSchema = z.object({
  name: z.string().trim().min(1, 'Nama variasi wajib diisi').max(120),
  values: z
    .array(z.string().trim().min(1, 'Nilai variasi wajib diisi').max(120))
    .min(1, 'Isi minimal satu nilai variasi')
    .max(100),
});

/** Satu baris daftar varian: kombinasi nilai + harga, stok, kode. */
export const skuRowSchema = z.object({
  /** Nilai per kelompok variasi, urut sesuai `variations`. */
  values: z.array(z.string().trim().min(1).max(120)).min(1).max(MAX_SKU_VARIATIONS),
  /** null berarti mengikuti harga produk. */
  price: z.number().min(0).max(1_000_000_000).nullable().default(null),
  /** Harga coret (sebelum diskon); diabaikan mesin harga bila tidak lebih tinggi dari harga. */
  compareAtPrice: z.number().min(0).max(1_000_000_000).nullable().default(null),
  stock: z.number().int().min(0).max(VARIANT_STOCK_MAX).default(0),
  sku: skuCodeSchema.default(''),
  isActive: z.boolean().default(true),
});

/** Isian produk yang ikut disimpan dari tab Upload SKU. */
export const skuProductSchema = z.object({
  name: z.string().trim().min(2, 'Nama produk minimal 2 karakter').max(160),
  description: z.string().trim().max(20_000),
  categorySlug: z.string().trim().min(1).max(120),
  basePrice: z.number().min(0).max(1_000_000_000),
  minQty: z.number().int().min(1).max(1_000_000),
  qtyStep: z.number().int().min(1).max(1_000_000),
  weightGram: z.number().int().min(0).max(1_000_000),
  /** Atribut = baris spesifikasi produk, sama dengan tab Detail. */
  specs: productSpecsSchema,
  /** Catatan operasional, sama dengan tab Detail. */
  notes: z.string().trim().max(10_000).default(''),
  /** Stok produk tanpa variasi; produk bervariasi memakai stok per baris varian. */
  stock: z.number().int().min(0).max(VARIANT_STOCK_MAX).default(0),
  lengthCm: z.number().int().min(0).max(10_000).nullable().default(null),
  widthCm: z.number().int().min(0).max(10_000).nullable().default(null),
  heightCm: z.number().int().min(0).max(10_000).nullable().default(null),
});

/**
 * Seluruh isian tab Upload SKU dalam satu simpan: data produk, variasi
 * (menjadi opsi + harga per kombinasi di mesin harga), stok per kombinasi,
 * dan isian marketplace.
 */
export const productSkuSaveSchema = z.object({
  product: skuProductSchema,
  /** Tombol "Simpan & Tampilkan" mengirim PUBLISHED, "Simpan & Arsipkan" mengirim ARCHIVED. */
  status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional(),
  tracksStock: z.boolean(),
  variations: z.array(skuVariationSchema).max(MAX_SKU_VARIATIONS).default([]),
  rows: z.array(skuRowSchema).max(MAX_SKU_ROWS).default([]),
  listing: marketplaceListingSchema,
});

/**
 * Produk baru dibuat langsung dari tab Upload SKU: isian yang sama ditambah
 * slug dan tipe produk, yang tidak bisa diubah lagi setelah produk ada.
 */
export const productSkuCreateSchema = productSkuSaveSchema.extend({
  slug: slugSchema,
  type: productTypeSchema.default('CONFIGURABLE'),
});

export type ProductSkuCreate = z.infer<typeof productSkuCreateSchema>;

export type SkuVariation = z.infer<typeof skuVariationSchema>;
export type SkuRow = z.infer<typeof skuRowSchema>;
export type SkuProductInput = z.infer<typeof skuProductSchema>;

export type ProductVariantInput = z.infer<typeof productVariantSchema>;
export type MarketplaceListing = z.infer<typeof marketplaceListingSchema>;
export type ProductSkuSave = z.infer<typeof productSkuSaveSchema>;

/** Kunci kombinasi yang stabil: dipakai mencocokkan varian dengan baris harga. */
export function variantKey(selections: Record<string, string>): string {
  return Object.keys(selections)
    .sort()
    .map((key) => `${key}=${selections[key] ?? ''}`)
    .join('|');
}

/** Video produk diunggah ke bucket publik, sama seperti foto. */
export const PRODUCT_VIDEO_MAX_BYTES = 50 * 1024 * 1024;

export const PRODUCT_VIDEO_TYPES: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
};

export const productVideoTicketSchema = z.object({
  mimeType: z.enum(['video/mp4', 'video/webm']),
  /** Diperiksa lagi di server setelah berkas benar-benar tersimpan. */
  sizeBytes: z.number().int().min(1).max(PRODUCT_VIDEO_MAX_BYTES, 'Video maksimal 50 MB'),
});

export const productVideoCommitSchema = z.object({ key: z.string().min(8).max(200) });

export type ProductVideoTicket = z.infer<typeof productVideoTicketSchema>;
export type ProductVideoCommit = z.infer<typeof productVideoCommitSchema>;
